import type {
  CreateMeetingDTO,
  ListMeetingActionsQuery,
  ListMeetingActionsResult,
  ListMeetingsQuery,
  ListMeetingsResult,
  MeetingDetailDTO,
  UpdateMeetingDTO,
} from "@/modules/management/meeting-minutes/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/management/meetings`;

function buildQuery(params: ListMeetingsQuery): string {
  const p = new URLSearchParams();
  if (params.offset != null) p.set("offset", String(params.offset));
  if (params.limit != null) p.set("limit", String(params.limit));
  if (params.sort != null) p.set("sort", JSON.stringify(params.sort));
  if (params.dateFrom != null) p.set("dateFrom", params.dateFrom);
  if (params.dateTo != null) p.set("dateTo", params.dateTo);
  if (params.search != null) p.set("search", params.search);
  if (params.createdBy != null) p.set("createdBy", params.createdBy);
  return p.toString();
}

function buildActionsQuery(params: ListMeetingActionsQuery): string {
  const p = new URLSearchParams();
  if (params.offset != null) p.set("offset", String(params.offset));
  if (params.limit != null) p.set("limit", String(params.limit));
  if (params.sort != null) p.set("sort", JSON.stringify(params.sort));
  if (params.dueDateFrom != null) p.set("dueDateFrom", params.dueDateFrom);
  if (params.dueDateTo != null) p.set("dueDateTo", params.dueDateTo);
  if (params.status != null) p.set("status", params.status);
  if (params.assigneeId != null) p.set("assigneeId", params.assigneeId);
  if (params.meetingId != null) p.set("meetingId", params.meetingId);
  return p.toString();
}

export const meetingMinutesApi = {
  list(query: ListMeetingsQuery): Promise<ListMeetingsResult> {
    const q = buildQuery(query);
    return fetcher.get<ListMeetingsResult>(q ? `${base}?${q}` : base);
  },

  getById(id: string): Promise<MeetingDetailDTO> {
    return fetcher.get<MeetingDetailDTO>(`${base}/${id}`);
  },

  listActions(query: ListMeetingActionsQuery): Promise<ListMeetingActionsResult> {
    const q = buildActionsQuery(query);
    return fetcher.get<ListMeetingActionsResult>(
      q ? `${base}/actions?${q}` : `${base}/actions`,
    );
  },

  create(dto: CreateMeetingDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(base, dto);
  },

  update(id: string, dto: UpdateMeetingDTO): Promise<{ ok: boolean }> {
    return fetcher.put<{ ok: boolean }>(`${base}/${id}`, dto);
  },

  delete(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(`${base}/${id}`);
  },

  updateActionStatus(
    meetingId: string,
    actionId: string,
    status: "pending" | "done",
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(
      `${base}/${meetingId}/actions/${actionId}/status`,
      { status },
    );
  },
};
